import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Download, FileText, List, Pencil, X } from 'lucide-react'
import { LangBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { EmptyState, Skeleton } from '@/components/ui/misc'
import {
  documentFileUrl,
  getDocument,
  getDocumentTerms,
  getDocumentText,
} from '@/lib/api/documents/get-documents'
import { cn, highlightTerms } from '@/lib/utils'
import { currentLocale } from '@/lib/i18n'
import { config } from '@/config'
import { DocumentActions } from './document-actions'
import { DocumentTermDrawer } from './document-term-drawer'

/**
 * The selected document's parsed text with its terms highlighted — the same
 * reading surface the preview offers, so a committed document and an
 * uncommitted one look and behave alike.
 */
export function DocumentText({
  documentId,
  collectionId,
  canEdit,
  jumpTerm,
  jumpPage,
  onEdit,
  onClose,
}: {
  documentId: number
  collectionId: number | null
  canEdit: boolean
  /** Term (lemma) to select on arrival, e.g. the one whose mention was clicked. */
  jumpTerm?: string
  /** Page to scroll to on arrival; with `jumpTerm`, its first hit on that page. */
  jumpPage?: number | null
  /** Opens the metadata dialog for this document. */
  onEdit?: () => void
  /** Deselects the document; the pane returns to the dropzone/empty state. */
  onClose?: () => void
}) {
  const { t } = useTranslation()
  // Which term is picked out of the running text, by term_id. Same interaction
  // as the preview: click a highlight and every occurrence of that term lifts
  // out of the surrounding ones.
  const [selected, setSelected] = useState<string | null>(null)
  // The drawer answers "what is this term?" without leaving the page. It is
  // separate from `selected` on purpose: closing it keeps the highlighting,
  // which is what the reader was using to scan the text.
  const [drawerFor, setDrawerFor] = useState<string | null>(null)
  // The document's terms as a list beside the text. Open by default: the
  // highlights alone do not tell a reader *which* terms extraction found, and
  // the Terms tab is the whole collection, not this one document.
  const [showList, setShowList] = useState(true)
  const text = useQuery({
    queryKey: ['document-text', documentId],
    queryFn: () => getDocumentText(documentId),
  })
  const terms = useQuery({
    queryKey: ['document-terms', documentId],
    queryFn: () => getDocumentTerms(documentId),
  })
  // Only for `has_file`; same key as the metadata dialog, so one fetch serves both.
  const detail = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => getDocument(documentId),
  })

  const surfaceToKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of terms.data ?? []) {
      const key = String(t.term_id)
      map.set(t.lemma.toLowerCase(), key)
      for (const m of t.mentions) if (m.surface) map.set(m.surface.toLowerCase(), key)
    }
    return map
  }, [terms.data])

  const segmented = useMemo(
    () =>
      (text.data ?? []).map((c) => ({
        page: c.page_number,
        segments: highlightTerms(c.text, surfaceToKey),
      })),
    [text.data, surfaceToKey],
  )

  const selectedTerm = (terms.data ?? []).find((t) => String(t.term_id) === selected)

  // Most-mentioned first, then alphabetical — the list doubles as a quick
  // "what is this document about" summary.
  const listed = useMemo(
    () =>
      [...(terms.data ?? [])].sort(
        (a, b) => b.mentions.length - a.mentions.length || a.lemma.localeCompare(b.lemma),
      ),
    [terms.data],
  )

  /** Clicking a term anywhere: lift its occurrences out of the text AND say
   *  what it is. A second click on the same term closes both. */
  const pickTerm = (key: string) => {
    setSelected((cur) => (cur === key ? null : key))
    setDrawerFor((cur) => (cur === key ? null : key))
  }

  /** Select from the list and bring the first occurrence into view. */
  const focusTerm = (key: string) => {
    pickTerm(key)
    if (key !== selected) {
      document
        .querySelector<HTMLElement>(`mark[data-term="${key}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  // A different document is a different set of terms, so a leftover selection
  // would point at a term that is not on screen any more.
  useEffect(() => {
    setSelected(null)
    setDrawerFor(null)
  }, [documentId])

  // Arriving from a mention: select that term and land on its page. Runs once
  // the text and the term list are both in, since the highlights come from
  // their join; after that the reader's own clicks take over.
  const ready = !!text.data && !!terms.data
  useEffect(() => {
    if (!ready || (!jumpTerm && !jumpPage)) return
    const key = jumpTerm ? surfaceToKey.get(jumpTerm.toLowerCase()) : undefined
    if (key) setSelected(key)
    // The marks are in the DOM only after this render commits.
    const id = requestAnimationFrame(() => {
      const pageSel = jumpPage ? `[data-page="${jumpPage}"]` : ''
      const target =
        (key && document.querySelector<HTMLElement>(`${pageSel} mark[data-term="${key}"]`)) ||
        (key && !jumpPage && document.querySelector<HTMLElement>(`mark[data-term="${key}"]`)) ||
        (pageSel && document.querySelector<HTMLElement>(pageSel)) ||
        null
      target?.scrollIntoView({ block: 'center' })
    })
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, documentId, jumpTerm, jumpPage])

  // Escape clears — cheaper than hunting for the highlight you clicked.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Escape closes the drawer first, the selection second — the same
      // order they were opened in.
      setDrawerFor((d) => (d ? null : d))
      setSelected((s) => (drawerFor ? s : null))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerFor])

  /**
   * The server builds the spreadsheet, so this is a plain link rather than a
   * fetch: the session cookie travels on its own, the browser handles the
   * download, and `Content-Disposition: attachment` means the page does not
   * navigate away. `lang` goes along because the UI language is a per-user
   * choice the API cannot otherwise know.
   */
  const exportUrl =
    `${config.BACKEND_URL}/documents/${documentId}/terms.xlsx` +
    `?lang=${encodeURIComponent(currentLocale())}`

  if (text.isLoading) {
    return (
      <div className="space-y-2 p-5">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
    )
  }
  if (!segmented.length) return <EmptyState title={t('documents.textLoadFailed')} />

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-accent/40 px-4 py-1.5 text-[11px] text-muted-foreground">
        <span>{t('documents.text')}</span>
        <span>·</span>
        <button
          type="button"
          onClick={() => setShowList((v) => !v)}
          className="rounded-sm hover:text-foreground"
          title={t(showList ? 'documents.hideTermList' : 'documents.showTermList')}
        >
          {t('documents.termsHighlighted', { count: terms.data?.length ?? 0 })}
        </button>
        {selectedTerm && (
          <>
            <span>·</span>
            <span className="font-medium text-foreground">{selectedTerm.lemma}</span>
            <span>{t('units.mentions', { count: selectedTerm.mentions.length })}</span>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
              {t('common.clearSelection')}
            </Button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          {[...new Set((terms.data ?? []).map((term) => term.lang))].slice(0, 4).map((l) => (
            <LangBadge key={l} lang={l} />
          ))}
          {/* The original PDF, when it was kept. Opens at the selected term's
              first page so the reader lands where the highlight is. */}
          {detail.data?.has_file && (
            <Tooltip label={t('documents.openPdf')}>
              <a
                href={documentFileUrl(
                  documentId,
                  selectedTerm?.mentions[0]?.page,
                  selectedTerm?.mentions[0]?.surface || selectedTerm?.lemma,
                )}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('documents.openPdf')}
                className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <FileText className="size-4" />
              </a>
            </Tooltip>
          )}
          {/* For editors the download lives in the actions menu; viewers never
              see that menu, so they keep this plain link — exporting reads
              nothing they cannot already see. */}
          {!canEdit && !!terms.data?.length && (
            <Tooltip label={t('documents.downloadXlsx')}>
              <a
                href={exportUrl}
                download
                aria-label={t('documents.downloadXlsx')}
                className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Download className="size-4" />
              </a>
            </Tooltip>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setShowList((v) => !v)}
            aria-pressed={showList}
            title={t(showList ? 'documents.hideTermList' : 'documents.showTermList')}
            aria-label={t(showList ? 'documents.hideTermList' : 'documents.showTermList')}
            className={cn(showList && 'bg-muted text-foreground')}
          >
            <List />
          </Button>
          {canEdit && onEdit && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onEdit}
              title={t('documents.editMetadata')}
              aria-label={t('documents.editMetadata')}
            >
              <Pencil />
            </Button>
          )}
          {canEdit && (
            // Actions for the document actually on screen — the bulk bar in the
            // list pane stays for checkbox selections.
            <DocumentActions
              selected={[documentId]}
              compact
              collectionId={collectionId}
              onClearSelection={() => {}}
            />
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              <X />
            </Button>
          )}
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="mx-auto max-w-3xl space-y-3 text-[13px] leading-relaxed">
            {/* pre-line: the page text carries one line break per PDF layout block,
                so headings, list items and table rows keep their own line. */}
            {segmented.map((chunk, ci) => (
              <p
                key={ci}
                className="whitespace-pre-line"
                data-page={chunk.page > 0 ? chunk.page : undefined}
              >
                {chunk.page > 0 && (
                  <span className="mr-2 select-none rounded bg-muted px-1 text-[10px] text-muted-foreground">
                    {t('common.page', { page: chunk.page })}
                  </span>
                )}
                {chunk.segments.map((seg, si) =>
                  seg.termKey ? (
                    <mark
                      key={si}
                      data-term={seg.termKey}
                      onClick={() => seg.termKey && pickTerm(seg.termKey)}
                      className={cn(
                        'cursor-pointer rounded-sm px-0.5',
                        seg.termKey === selected
                          ? 'bg-primary font-semibold text-primary-foreground'
                          : 'bg-highlight/50 text-highlight-foreground hover:bg-highlight',
                      )}
                    >
                      {seg.text}
                    </mark>
                  ) : (
                    <span key={si}>{seg.text}</span>
                  ),
                )}
              </p>
            ))}
          </div>
        </div>
        {/* Opened by a click on any term, in the text or in the list. */}
        <DocumentTermDrawer
          term={(terms.data ?? []).find((x) => String(x.term_id) === drawerFor) ?? null}
          collectionId={collectionId}
          onClose={() => setDrawerFor(null)}
        />
        {showList && (
          <aside className="flex w-64 shrink-0 flex-col border-l border-border">
            <div className="shrink-0 border-b border-border px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('documents.termList')}
              </div>
              <div className="text-[11px] text-muted-foreground">{t('documents.termListHint')}</div>
            </div>
            <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto py-1">
              {terms.isLoading ? (
                <div className="space-y-2 p-3">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-4 w-full" />
                  ))}
                </div>
              ) : !listed.length ? (
                <div className="p-3 text-[12px] text-muted-foreground">
                  {t('documents.termListEmpty')}
                </div>
              ) : (
                listed.map((term) => {
                  const key = String(term.term_id)
                  const isSelected = key === selected
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => focusTerm(key)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-accent',
                        isSelected && 'bg-accent',
                      )}
                    >
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-[13px]',
                          isSelected ? 'font-semibold text-accent-foreground' : 'font-medium',
                        )}
                      >
                        {term.lemma}
                      </span>
                      <LangBadge lang={term.lang} />
                      <span className="w-6 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
                        {term.mentions.length}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  )
}
